import { Result, ResultVariants } from '@dexma/ui-components';
import { StyledTable } from 'adapter/in/common/components/HomeTable/style';
import Bot from 'asset/images/bot.svg';
import i18n from 'asset/translation';
import React, { memo, ReactElement, useLayoutEffect, useState } from 'react';

const PAGE_SIZE = 10;

export type HomeTableProps = {
    columns: any | null;
    dataSource: any[] | null;
    isLoading: boolean;
    showError: boolean;
    onChange: (
        pagination: never,
        filters: never,
        sorter: { column: { columnKey: string }; order: 'ascend' | 'descend' },
        extra: { currentDataSource: []; action: 'paginate' | 'sort' | 'filter' }
    ) => void;
    onRow?: (record: any) => React.HTMLAttributes<HTMLElement> & React.TdHTMLAttributes<HTMLElement>;
    resetPageKey?: string;
};

const HomeTable = (props: HomeTableProps): ReactElement => {
    const { columns, dataSource, isLoading, showError, onChange, onRow, resetPageKey } = props;
    const [currentPage, setCurrentPage] = useState(1);
    const [resetToken, setResetToken] = useState(0);

    const onPageChange = (page: number) => setCurrentPage(page);

    useLayoutEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
            setResetToken((token) => token + 1);
        }
    }, [resetPageKey]);

    const dataSourcePaginated = () => {
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        return dataSource?.slice(startIndex, startIndex + PAGE_SIZE)?.map((source) => ({ ...source, key: source.id }));
    };

    return (
        <StyledTable
            key={resetToken}
            columns={columns}
            dataSource={dataSourcePaginated()}
            isLoading={isLoading}
            showError={showError}
            onRow={onRow}
            locale={{
                triggerAsc: i18n.t('dashboards.sort.ascend', { column: i18n.t('common.name') }),
                triggerDesc: i18n.t('dashboards.sort.descend', { column: i18n.t('common.name') }),
            }}
            errorContent={
                <Result
                    variant={ResultVariants.DEFAULT}
                    title={i18n.t('common.errorWrong')}
                    info={i18n.t('common.errorWrongInfo')}
                    iconElement={<Bot />}
                />
            }
            onChange={onChange as any}
            currentPage={currentPage}
            totalRegisters={dataSource ? dataSource.length : 0}
            pageSize={PAGE_SIZE}
            onPageChange={onPageChange}
            prevPageAriaLabel={i18n.t('common.ariaLabel.tablePagination.prevPage')}
            prevDotsPageAriaLabel={i18n.t('common.ariaLabel.tablePagination.prevDotsPage')}
            nextDotsPageAriaLabel={i18n.t('common.ariaLabel.tablePagination.nextDotsPage')}
            nextPageAriaLabel={i18n.t('common.ariaLabel.tablePagination.nextPage')}
        />
    );
};

export default memo(HomeTable);
